import { useEffect, useState } from "react";
import { useAction, useTrouble } from "../App";
import { api, botName, clear } from "../api/client";
import type {
  ApprovalView,
  DashboardView,
  KillSwitchView,
  LogLineView,
  RunView,
  StepView,
} from "../api/types";
import {
  Apparatus,
  Banner,
  Diff,
  Empty,
  Figure,
  LaneDot,
  LogLines,
  RuledRow,
  wordFor,
} from "../components/Parts";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  between,
  grouped,
  now as rightNow,
  percent,
  remaining,
  short,
  until,
} from "../lib/format";
import { Alert } from "@/components/ui/alert";

/** How many lines a screen holds; a long run writes far more than anyone scrolls back through. */
const KEPT = 200;

/* What a lane's progress bar is painted in. A class, because the bar's fill is a shadcn slot. */
const COLOURS: Record<string, string> = {
  RUNNING: "bg-running",
  QUEUED: "bg-queued",
  WAITING_APPROVAL: "bg-waiting",
  FAILED: "bg-destructive",
  DONE: "bg-muted-foreground",
};

/** What is happening now: anything waiting on you, then the lanes, then what is next. */
export function Now({ now }: { now: DashboardView | null }) {
  if (!now) return <Empty>Reading what the bots are doing.</Empty>;

  // The switch holds every lane at once, so it is a fact about the screen rather than about one run.
  const held = !clear(now.killSwitch);
  const lanes = now.lanes ?? [];
  const running = lanes.find((lane) => lane.status === "RUNNING");

  return (
    <>
      {held ? <Held state={now.killSwitch} /> : null}

      {(now.approvals ?? []).map((approval) => (
        <Waiting key={approval.id} approval={approval} />
      ))}

      <section className="flex flex-col gap-4.5">
        <Apparatus>Lanes</Apparatus>
        {lanes.length === 0 ? (
          <Card>
            <CardContent>
              <Empty>No bot is running.</Empty>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4.5 [grid-template-columns:repeat(auto-fit,minmax(340px,1fr))]">
            {lanes.map((lane) => (
              <Lane key={lane.id} run={lane} held={held} />
            ))}
          </div>
        )}
      </section>

      {/* Two columns only when there is a log to fill the first. An empty one still holds its
          grid column, which left "Next up" stranded against the right edge of an idle screen. */}
      {running ? (
        <div className="flex items-start gap-8 max-sm:flex-col max-sm:gap-6">
          <div className="flex min-w-0 flex-1 flex-col gap-2.5">
            <LiveLog run={running} />
          </div>
          <div className="flex w-85 flex-none flex-col gap-2.5 max-sm:w-full">
            <NextUp now={now} />
          </div>
        </div>
      ) : (
        <section className="flex flex-col gap-4.5">
          <NextUp now={now} />
        </section>
      )}
    </>
  );
}

/**
 * Why nothing is moving, when the reason is the kill switch.
 *
 * A held lane looks a lot like an idle one, and an operator who cannot tell them apart goes looking
 * for a bug.
 */
function Held({ state }: { state: KillSwitchView }) {
  const onPurpose = state.reachable;
  return (
    <Banner
      title={
        onPurpose ? "The bots are stopped" : "The kill switch has not been read"
      }
      tone={onPurpose ? "waiting" : "failed"}
    >
      Every lane is held because {state.detail}.{" "}
      {onPurpose
        ? `They start again when ${state.page} says false.`
        : `Nothing saves until ${state.page} can be read.`}
    </Banner>
  );
}

/** A question a bot stopped to ask, shown above everything until it is answered. */
function Waiting({ approval }: { approval: ApprovalView }) {
  const [deciding, run] = useAction();

  return (
    <Alert
      role="status"
      className="flex flex-col gap-3 border-0 border-l-2 border-l-waiting bg-card"
    >
      <div className="apparatus text-waiting">
        {botName(approval)} is waiting on you
      </div>
      <h2 className="font-serif text-title font-medium -tracking-[0.015em]">
        {approval.title}
      </h2>
      <p className="m-0 max-w-[60ch] text-pretty text-muted-foreground">
        {approval.summary}
      </p>
      {approval.diff ? <Diff diff={approval.diff} /> : null}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          disabled={deciding}
          onClick={() => run(() => api.decide(approval, true))}
        >
          {approval.accept}
        </Button>
        <Button
          variant="destructive"
          disabled={deciding}
          onClick={() => run(() => api.decide(approval, false))}
        >
          {approval.decline}
        </Button>
      </div>
    </Alert>
  );
}

/** One bot's lane: what it is, how far it has got, and how fast. */
function Lane({ run, held }: { run: RunView; held: boolean }) {
  const [cancelling, act] = useAction();

  const lookedAt = run.lookedAt ?? 0;
  const total = run.total ?? 0;
  const changed = run.changed ?? 0;
  const saved = run.saved ?? 0;
  const rate = run.rate ?? null;
  // Nothing expected yet reads as nothing done, not as everything done. A lane only ever shows a run
  // that is still going, and a full bar over the word "starting" says the opposite of what is true —
  // easy to miss until the step's own bar appeared beneath it, empty, and the two disagreed.
  const fraction = total > 0 ? lookedAt / total : 0;
  const elapsed = between(run.startedAt ?? run.queuedAt, rightNow());

  return (
    <Card>
      <CardContent className="flex flex-col gap-2.5">
        <div className="flex items-center gap-2.5">
          <LaneDot status={run.status} held={held} />
          <span className="font-serif text-lane font-medium -tracking-[0.01em]">
            {botName(run)}
          </span>
          <span
            className={`apparatus ml-auto ${
              held
                ? "text-muted-foreground"
                : run.status === "RUNNING"
                  ? "text-running"
                  : "text-waiting"
            }`}
          >
            {held ? "Held" : wordFor(run.status)}
          </span>
        </div>

        <div className="font-mono text-mono text-muted-foreground">
          run {grouped(run.id)} · {run.taskName ?? "one-off"}
        </div>

        <div className="flex gap-6 *:flex-1">
          <Figure value={grouped(lookedAt)} label="Looked at" />
          <Figure
            value={grouped(changed)}
            label={`Changed ${percent(changed, lookedAt)}`}
          />
          <Figure
            value={grouped(saved)}
            label="Saved"
            tone={saved > 0 ? "running" : "muted"}
          />
          {/* Only once there are some: a lane is a card in a grid, and a fourth zero is noise. */}
          {run.notAttempted > 0 ? (
            <Figure
              value={grouped(run.notAttempted)}
              label="Not attempted"
              tone="waiting"
            />
          ) : null}
        </div>

        {/* Written as soon as the run decides to stop, so the lane says why while it winds down. */}
        {run.stopReason ? (
          <p className="m-0 text-row text-pretty text-waiting">
            Stopping: {run.stopReason}
          </p>
        ) : null}

        <div className="flex justify-end">
          <Button
            variant="destructive"
            disabled={cancelling}
            onClick={() => act(() => api.cancel(run.id))}
          >
            {cancelling ? "Stopping" : "Stop"}
          </Button>
        </div>

        <Progress
          value={Math.min(100, Math.max(0, fraction * 100))}
          indicatorClassName={
            held ? "bg-input" : (COLOURS[run.status] ?? "bg-muted-foreground")
          }
        />

        <div className="flex justify-between font-mono text-mono text-muted-foreground">
          <span>
            {rate === null
              ? "starting"
              : `${Math.round(fraction * 100)}% · ${grouped(rate)}/s`}
          </span>
          <span>{remaining(lookedAt, total, rate) ?? short(elapsed)}</span>
        </div>

        <Doing step={deepest(run.steps ?? [])} held={held} />
      </CardContent>
    </Card>
  );
}

/**
 * The step the run is on, or nothing when it is not in one.
 *
 * A lane sends only the steps still going, so the deepest of them is the innermost phase: `German` with
 * `editing` inside it answers `editing`. Where several are open at one depth — which happens if a bot opens
 * a step per parallel worker rather than one for the phase — the most recently started wins, because a bar
 * that flickers between three names says less than a bar that names one.
 */
function deepest(steps: StepView[]): StepView | null {
  if (steps.length === 0) return null;

  const inner = steps.filter(
    (step) => !steps.some((other) => other.parent === step.id),
  );
  return inner.reduce((latest, step) =>
    step.startedAt >= latest.startedAt ? step : latest,
  );
}

/**
 * What the run is doing, under its own bar.
 *
 * One line and one bar whatever the step count, because a lane is a card in a grid and a thirty-six language
 * run would otherwise be thirty-six bars tall. The whole list is on the run's own page.
 */
function Doing({ step, held }: { step: StepView | null; held: boolean }) {
  if (step === null) return null;

  const lookedAt = step.lookedAt ?? 0;
  const total = step.total ?? 0;
  const fraction = total > 0 ? lookedAt / total : 0;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline gap-2 text-row text-muted-foreground">
        <span className="text-foreground">{step.name}</span>
        <span className="flex-1" />
        <span className="font-mono text-mono">
          {total > 0
            ? `${grouped(lookedAt)} / ${grouped(total)}`
            : grouped(lookedAt)}
        </span>
      </div>
      <Progress
        value={Math.min(100, Math.max(0, fraction * 100))}
        indicatorClassName={held ? "bg-input" : "bg-muted-foreground"}
      />
    </div>
  );
}

/** What the running lane is saying, kept up to date. */
function LiveLog({ run }: { run: RunView }) {
  const [lines, setLines] = useState<LogLineView[]>([]);
  const [seen, setSeen] = useState(0);
  const report = useTrouble();

  useEffect(() => {
    setLines([]);
    setSeen(0);
  }, [run.id]);

  useEffect(() => {
    let live = true;
    api
      .log(run.id, seen)
      .then((all) => {
        if (!live) return;
        // Only what is genuinely new. `since` is a request, not a guarantee, and the rows are keyed
        // by seq -- a repeat would render twice under one key and be silently mis-tracked.
        const fresh = all.filter((line) => line.seq > seen);
        if (fresh.length === 0) return;
        setLines((held) => [...held, ...fresh].slice(-KEPT));
        setSeen(Math.max(...fresh.map((line) => line.seq)));
      })
      .catch(report);
    return () => {
      live = false;
    };
    // Re-asks whenever the dashboard poll moves the run on, which is what keeps the log live.
  }, [run.id, run.lookedAt, run.status, seen, report]);

  return (
    <>
      <Apparatus>Live log — {botName(run)}</Apparatus>
      {lines.length === 0 ? (
        <Empty>Nothing said yet.</Empty>
      ) : (
        <LogLines lines={lines} />
      )}
    </>
  );
}

/** What is queued behind the lanes, and what fires next. */
function NextUp({ now }: { now: DashboardView }) {
  const queued = now.queued ?? [];
  const scheduled = [...(now.scheduled ?? [])].sort(
    (a, b) =>
      (a.nextFiringAt ?? Number.MAX_SAFE_INTEGER) -
      (b.nextFiringAt ?? Number.MAX_SAFE_INTEGER),
  );
  const moment = rightNow();

  return (
    <>
      <Apparatus>Next up</Apparatus>
      {queued.length === 0 && scheduled.length === 0 ? (
        <Empty>Nothing queued, nothing scheduled.</Empty>
      ) : (
        <div>
          {queued.map((run) => (
            <RuledRow
              key={run.id}
              label={`${botName(run)} · ${run.taskName ?? "one-off"}`}
              value="queued"
              tone="text-muted-foreground"
            />
          ))}
          {scheduled.map((task, index) => (
            <RuledRow
              key={task.id ?? task.name}
              label={task.name}
              value={
                task.nextFiringAt
                  ? until(task.nextFiringAt, moment)
                  : "not scheduled"
              }
              tone="text-muted-foreground"
              last={index === scheduled.length - 1}
            />
          ))}
        </div>
      )}
    </>
  );
}
